/**
 * Image card — the grid tile shared by the /images library and the entity
 * workbench's Images tab.
 *
 * Extracted from ImagesPage 2026-08-13 when the workbench gained its own
 * gallery: two renderings of the role/eligibility model would have drifted,
 * and the whole point of that model is that it reads the same everywhere.
 */
import type { ImageSummary } from '../../lib/api/types.js';
import { ZoomButton } from './ImageLightbox.js';

// Postgres NUMERIC(3,2) columns come through asyncpg → JSON as strings
// (e.g. "0.13"). Coerce defensively before calling .toFixed.
export function fmtNum(v: unknown, digits = 2): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n.toFixed(digits) : '—';
}

export function ImageCard(props: {
  image: ImageSummary;
  onClick: () => void;
  /** When given, a magnifier appears on hover and opens the full-size
   *  preview — deliberately a sibling of the tile button, so previewing
   *  never triggers the tile's own action. */
  onZoom?: () => void;
}) {
  const { image } = props;
  return (
    <div className="group relative">
    {props.onZoom && <ZoomButton onClick={props.onZoom} />}
    <button
      onClick={props.onClick}
      className="w-full text-left bg-surface border border-gray-200 rounded-lg overflow-hidden hover:border-blue-400 hover:shadow-md transition"
    >
      <div className="aspect-square bg-gray-100 flex items-center justify-center overflow-hidden">
        <img
          src={image.cdn_url}
          alt={image.alt_text ?? ''}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition"
        />
      </div>
      <div className="p-2 text-xs space-y-1">
        <div className="flex items-center justify-between">
          <span className="font-medium text-gray-800">{image.image_type}</span>
          {image.low_confidence && (
            <span className="px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800">
              low conf
            </span>
          )}
        </div>
        <div className="flex items-center justify-between text-gray-500">
          <span>{image.image_role ?? '—'}</span>
          <span>{image.quality_score != null ? `q${fmtNum(image.quality_score)}` : ''}</span>
        </div>
        <div className="text-gray-400 truncate">{image.source_type}</div>
      </div>
    </button>
    </div>
  );
}

/** Lightbox item for one library image — `cdn_url` IS the original object,
 *  the tile just renders it small. */
export function toLightboxItem(image: ImageSummary) {
  return {
    url: image.cdn_url,
    alt: image.alt_text,
    title: image.caption || image.alt_text || image.image_type,
    subtitle: [
      image.image_role ?? 'no role',
      image.image_type,
      image.quality_score != null ? `q${fmtNum(image.quality_score)}` : null,
      image.width && image.height ? `${image.width}×${image.height}` : null,
      image.source_type,
    ].filter(Boolean).join(' · '),
  };
}
